/**
 * Module dependencies.
 */

import * as React from 'react';
import { Check, ChevronDown, Loader2, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from 'app/components/ui/popover';
import { cn } from 'app/lib/utils';

/**
 * Types.
 */

type OptionValue = string | number;

export type ComboboxOption = {
  value: OptionValue;
  label: React.ReactNode;
  // Extra fields (e.g. the `optionLabelProp` used for the selected display).
  [key: string]: any;
};

export type ComboboxProps = {
  options: ComboboxOption[];
  value: OptionValue | OptionValue[] | null | undefined;
  onChange: (value: any) => void;
  multiple?: boolean;
  searchable?: boolean;
  allowClear?: boolean;
  loading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  filterOption?: (input: string, option: ComboboxOption) => boolean;
  triggerClassName?: string;
  // Which option field to show for the selected value in the trigger (the
  // dropdown still renders `label`). Mirrors antd's `optionLabelProp`.
  optionLabelProp?: string;
};

/**
 * Export `Combobox` component.
 *
 * A single/multi select on Popover — covers the antd Select surface used across
 * the app: searchable (showSearch), multiple (mode), allowClear, loading and a
 * custom filterOption. onChange emits the option's original value(s).
 */

export const Combobox = React.forwardRef<HTMLButtonElement, ComboboxProps>(function Combobox(
  {
    options,
    value,
    onChange,
    multiple,
    searchable = true,
    allowClear,
    loading,
    disabled,
    placeholder = 'Select…',
    filterOption,
    triggerClassName,
    optionLabelProp
  },
  ref
) {
  // The value to show in the trigger for a given option.
  const triggerLabel = (option: ComboboxOption) =>
    optionLabelProp && option[optionLabelProp] != null ? option[optionLabelProp] : option.label;
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');

  // When the dropdown is inside a Dialog, Radix's scroll lock
  // (react-remove-scroll) swallows wheel events over the portalled list. A
  // callback ref attaches a non-passive wheel listener the moment the list
  // mounts, scrolling it manually so the mouse wheel works everywhere.
  const listRef = React.useCallback((node: HTMLDivElement | null) => {
    if (!node) {
      return;
    }

    node.addEventListener(
      'wheel',
      (event: WheelEvent) => {
        if (node.scrollHeight > node.clientHeight) {
          event.stopPropagation();
          event.preventDefault();
          node.scrollTop += event.deltaY;
        }
      },
      { passive: false }
    );
  }, []);

  const values = multiple ? (Array.isArray(value) ? value : value == null ? [] : [value]) : [];
  const selected = new Set(values.map(String));

  const filtered = React.useMemo(() => {
    if (!searchable || !query) {
      return options;
    }

    const lowered = query.toLowerCase();

    return options.filter(option =>
      filterOption
        ? filterOption(query, option)
        : String(option.label ?? '')
            .toLowerCase()
            .includes(lowered)
    );
  }, [options, query, searchable, filterOption]);

  const selectedSingle = !multiple ? options.find(option => String(option.value) === String(value)) : undefined;
  const hasValue = multiple ? values.length > 0 : value != null && value !== '';

  const pick = (option: ComboboxOption) => {
    if (multiple) {
      const key = String(option.value);

      onChange(selected.has(key) ? values.filter(item => String(item) !== key) : [...values, option.value]);

      return;
    }

    onChange(option.value);
    setOpen(false);
    setQuery('');
  };

  return (
    <Popover
      onOpenChange={next => {
        setOpen(next);

        if (!next) {
          setQuery('');
        }
      }}
      open={open}
    >
      <PopoverTrigger asChild>
        <button
          className={cn(
            'group flex h-10 min-h-10 w-full items-center justify-between gap-1.5 rounded-md border border-input bg-background px-3 text-sm shadow-sm outline-none transition-colors data-[state=open]:border-ring/40 disabled:cursor-not-allowed disabled:opacity-50',
            triggerClassName
          )}
          disabled={disabled}
          ref={ref}
          type={'button'}
        >
          <span className={'flex min-w-0 flex-1 flex-wrap items-center gap-1 py-1.5 text-left'}>
            {multiple ? (
              values.length === 0 ? (
                <span className={'text-muted-foreground'}>{placeholder}</span>
              ) : (
                options
                  .filter(option => selected.has(String(option.value)))
                  .map(option => (
                    <span
                      className={'inline-flex items-center gap-1 rounded bg-secondary px-1.5 py-0.5 text-xs'}
                      key={String(option.value)}
                    >
                      {triggerLabel(option)}

                      <X
                        className={'h-3 w-3 cursor-pointer text-muted-foreground hover:text-foreground'}
                        onClick={event => {
                          event.preventDefault();
                          event.stopPropagation();
                          onChange(values.filter(item => String(item) !== String(option.value)));
                        }}
                      />
                    </span>
                  ))
              )
            ) : selectedSingle ? (
              <span className={'truncate'}>{triggerLabel(selectedSingle)}</span>
            ) : (
              <span className={'text-muted-foreground'}>{placeholder}</span>
            )}
          </span>

          <span className={'relative flex h-4 w-4 shrink-0 items-center justify-center'}>
            {loading ? (
              <Loader2 className={'h-4 w-4 animate-spin text-muted-foreground'} />
            ) : (
              <>
                {allowClear && hasValue && !disabled && (
                  <span
                    aria-label={'Clear'}
                    className={
                      'absolute inset-0 hidden place-items-center text-muted-foreground transition-colors hover:text-foreground group-hover:grid'
                    }
                    onClick={event => {
                      event.preventDefault();
                      event.stopPropagation();
                      onChange(multiple ? [] : undefined);
                    }}
                    onPointerDown={event => {
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                    role={'button'}
                  >
                    <X className={'h-4 w-4'} />
                  </span>
                )}

                <ChevronDown
                  className={cn('h-4 w-4 opacity-50', allowClear && hasValue && !disabled && 'group-hover:hidden')}
                />
              </>
            )}
          </span>
        </button>
      </PopoverTrigger>

      <PopoverContent align={'start'} className={'w-[--radix-popover-trigger-width] min-w-[200px] p-0'}>
        {searchable && (
          <div className={'border-b border-border p-2'}>
            <input
              autoFocus
              className={'w-full bg-transparent px-1 text-sm outline-none placeholder:text-muted-foreground'}
              onChange={event => setQuery(event.target.value)}
              placeholder={'Search…'}
              value={query}
            />
          </div>
        )}

        <div className={'max-h-64 overflow-y-auto p-1'} ref={listRef}>
          {loading && <div className={'px-2 py-3 text-center text-sm text-muted-foreground'}>{'Loading…'}</div>}

          {!loading && filtered.length === 0 && (
            <div className={'px-2 py-3 text-center text-sm text-muted-foreground'}>{'No results'}</div>
          )}

          {!loading &&
            filtered.map(option => {
              const checked = multiple ? selected.has(String(option.value)) : String(option.value) === String(value);

              return (
                <button
                  className={cn(
                    'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none transition-colors hover:bg-muted focus-visible:bg-muted',
                    checked && !multiple && 'bg-muted/60'
                  )}
                  key={String(option.value)}
                  onClick={() => pick(option)}
                  type={'button'}
                >
                  {multiple && (
                    <span
                      className={cn(
                        'grid h-4 w-4 shrink-0 place-items-center rounded-sm border',
                        checked ? 'border-primary bg-primary text-primary-foreground' : 'border-input'
                      )}
                    >
                      {checked && <Check className={'h-3 w-3'} />}
                    </span>
                  )}

                  <span className={'min-w-0 flex-1 truncate'}>{option.label}</span>

                  {!multiple && checked && <Check className={'h-4 w-4 shrink-0 text-primary'} />}
                </button>
              );
            })}
        </div>
      </PopoverContent>
    </Popover>
  );
});
